<template>
  <LightBox
    :display="display"
    :fullscreen="props.fullscreen"
    @close="handleClose"
  >
    <template #header>
      <div class="absolute top-6 right-6 flex items-center gap-2">
        <BaseButtonOld
          v-if="lightbox && !isTable"
          :href="lightbox.src"
          color="primary"
        >
          <FontAwesomeIcon :icon="['fas', 'external-link']" />
          <span>{{ $t('lightbox.open') }}</span>
        </BaseButtonOld>

        <BaseButtonOld color="white" @click="handleClose">
          {{ $t('lightbox.close') }}
        </BaseButtonOld>
      </div>
    </template>
    <div
      v-if="lightbox"
      class="flex items-center justify-center"
      :class="{ 'w-full h-full': props.fullscreen }"
    >
      <vue-zoomer
        v-if="lightbox.type === 'img'"
        ref="zoomer"
        class="w-full h-full bg-white"
      >
        <img
          :src="lightbox.src"
          draggable="false"
          class="w-full object-contain max-h-[75vh]"
        />
      </vue-zoomer>
      <iframe
        v-else-if="lightbox.type === 'embed'"
        class="w-[90vw] h-full"
        :src="lightbox.src"
        frameborder="0"
        allowfullscreen="true"
        allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
      >
      </iframe>
      <div v-else-if="isTable" class="max-w-[90vw] max-h-[70vh]">
        <BaseEditor
          class="bg-white enlarge-table-editor"
          :content="lightbox.src as string"
          :editable="false"
          :handle-fetch-inline-content="handleFetchInlineContent"
          :handle-fetch-inline-mention="handleFetchInlineMention"
          @go-to-content="handleGoToContent"
        />
      </div>
    </div>
    <template #footer v-if="lightbox && lightbox.type === 'img'">
      <div class="absolute bottom-8 flex items-center gap-4">
        <BaseButtonOld size="md" square @click="zoomOut" color="primary">
          <FontAwesomeIcon :icon="['fas', 'minus']" />
        </BaseButtonOld>

        <BaseButtonOld size="md" square @click="zoomIn" color="primary">
          <FontAwesomeIcon :icon="['fas', 'plus']" />
        </BaseButtonOld>
      </div>
    </template>
  </LightBox>
</template>

<script setup lang="ts">
import { useKnowledgeStore, useAdminStore } from '@getmayday/vue-modules';
import VueZoomer from 'vue-zoomer/src/vue-zoomer.vue';
import LightBox from './LightBox.vue';

const configStore = useConfigStore();
const knowledgeStore = useKnowledgeStore();
const adminStore = useAdminStore();

const { lightbox } = storeToRefs(configStore);
const { goToEntity } = useEntities();

const display = ref(false);

const props = defineProps<{ fullscreen?: boolean }>();

const handleClose = () => {
  configStore.setImageUrl(null);
};

onMounted(() => {
  configStore.setImageUrl(null);
});

watch(lightbox, (newVal) => {
  display.value = !!newVal;
});

// Zoomer
const zoomer = ref<any | null>(null);

const zoomIn = () => {
  zoomer.value?.zoomIn();
};

const zoomOut = () => {
  zoomer.value?.zoomOut();
};

const isTable = computed(() => lightbox.value?.type === 'table');

const handleFetchInlineContent = async (
  id: string,
  lang: string,
  type: string,
) => {
  if (type === 'Case') {
    return knowledgeStore.getCase(id, lang);
  }
  return knowledgeStore.getContent(id, lang);
};
const handleFetchInlineMention = adminStore.handleFetchInlineMention;

const handleGoToContent = (content: any) => {
  goToEntity({ ...content, fromModal: true, showBack: true });
  handleClose();
};
</script>

<style scoped>
.test-enter-active,
.test-leave-active {
  display: flex;
}

.test-enter-from,
.test-leave-to {
  display: none;
}

:deep() .enlarge-table-editor {
  .table-menu {
    display: none !important;
  }

  table {
    margin: 0;
  }
}
</style>
